import React, { useState, useEffect } from 'react';
import { XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { api } from '../../config/api';
import { BookContextItem } from '../../hooks/useBookContext';
import { cacheBookMapping } from '../../utils/bookIdResolver';

interface Book {
  id: string;  // UUID from backend
  ref_id: number;  // Agent-friendly integer ID
  title: string;
  author: string;
  category: string;
  cover_presigned_url?: string;
}

interface BookSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedBooks: BookContextItem[];
  onApply: (books: BookContextItem[]) => void;
}

export const BookSelectionModal: React.FC<BookSelectionModalProps> = ({
  isOpen,
  onClose,
  selectedBooks,
  onApply
}) => {
  const [books, setBooks] = useState<Book[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [tempSelectedBooks, setTempSelectedBooks] = useState<BookContextItem[]>([]);

  // Load categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await api.get('/api/books/categories');
        setCategories(['all', ...response.data]);
      } catch (error) {
        console.error('Failed to fetch categories:', error);
      }
    };
    fetchCategories();
  }, []);

  // Load books when category changes
  useEffect(() => {
    const fetchBooks = async () => {
      setLoading(true);
      try {
        const params: any = {};
        if (selectedCategory !== 'all') {
          params.category = selectedCategory;
        }
        const response = await api.get('/api/books/', { params });
        setBooks(response.data);

        // Pre-populate ref_id → UUID cache for page link resolution
        // This allows MarkdownRenderer to resolve ref_id links instantly
        response.data.forEach((book: Book) => {
          if (book.ref_id && book.id) {
            cacheBookMapping(book.ref_id, book.id);
          }
        });
      } catch (error) {
        console.error('Failed to fetch books:', error);
      } finally {
        setLoading(false);
      }
    };
    if (isOpen) {
      fetchBooks();
    }
  }, [selectedCategory, isOpen]);

  // Initialize temp selection when modal opens
  useEffect(() => {
    if (isOpen) {
      setTempSelectedBooks(selectedBooks);
    }
  }, [isOpen, selectedBooks]);

  // Single-select: clicking a different book REPLACES the current
  // selection; clicking the already-selected book deselects it.
  // The general chat enforces "exactly one book" so we never let the
  // modal accumulate multiple.
  const toggleBookSelection = (book: Book) => {
    const isSelected = tempSelectedBooks.some(b => b.id === book.id);
    if (isSelected) {
      setTempSelectedBooks([]);
    } else {
      setTempSelectedBooks([{
        id: book.id,
        title: book.title,
        category: book.category,
      }]);
    }
  };

  const handleApply = () => {
    onApply(tempSelectedBooks);
    onClose();
  };

  const filteredBooks = books.filter(book =>
    book.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    book.author.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[85vh] md:max-h-[90vh] flex flex-col mx-2 md:mx-0"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg md:text-xl font-semibold text-gray-900 dark:text-white">
            Choose a book
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title or author..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="py-3 border-b border-gray-200 dark:border-gray-700 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex gap-2.5 px-4">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-3.5 py-2 text-sm rounded-lg whitespace-nowrap transition-colors flex-shrink-0 ${
                  selectedCategory === category
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {category.replace(/_/g, ' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Books Grid */}
        <div className="flex-1 overflow-y-auto p-4 md:p-5">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
          ) : filteredBooks.length === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
              No books found
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {filteredBooks.map(book => {
                const isSelected = tempSelectedBooks.some(b => b.id === book.id);
                return (
                  <div
                    key={book.id}
                    onClick={() => toggleBookSelection(book)}
                    className={`cursor-pointer p-4 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {book.cover_presigned_url && (
                        <img
                          src={book.cover_presigned_url}
                          alt={book.title}
                          className="w-16 h-20 object-cover rounded"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                          {book.title}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400 truncate">
                          {book.author}
                        </p>
                        <span className="inline-block mt-1 px-2 py-0.5 text-xs rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          {book.category}
                        </span>
                      </div>
                      {isSelected && (
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center">
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
          <span className="text-sm text-gray-600 dark:text-gray-400 truncate pr-3 max-w-[55%]">
            {tempSelectedBooks.length === 0
              ? 'No book selected'
              : `Selected: ${tempSelectedBooks[0].title}`}
          </span>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 transition-colors"
            >
              Apply
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
